import { useEffect, useState } from "react";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { API_BASE } from "../../../config";
import MeasuredChartFrame from "./MeasuredChartFrame";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const TICK = { fill: "#d6d5ea", fontSize: 12 };
const PURPLE = "#6F3AFF";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatDays(value) {
  return `${Number(value ?? 0).toFixed(1)}d`;
}

function formatLabel(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function normalizeRows(raw) {
  const source = raw ?? {};
  const rows = Array.isArray(source)
    ? source
    : Array.isArray(source.data)
      ? source.data
      : Array.isArray(source.trend)
        ? source.trend
        : Array.isArray(source.weeks)
          ? source.weeks
          : Array.isArray(source.items)
            ? source.items
            : Array.isArray(source.rows)
              ? source.rows
              : [];

  return rows.map((row) => ({
    period: row.week ?? row.date ?? row.period ?? row.label ?? "",
    avgDurationDays: toNumber(
      row.avg_duration_days ??
      row.average_duration_days ??
      row.duration_days ??
      row.avg_days ??
      row.days ??
      row.value,
      0
    ),
  }));
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="remediation-dashboard-tooltip">
      <div className="remediation-dashboard-tooltip-title">
        {formatLabel(label)}
      </div>

      <div>
        Avg. duration: <strong>{formatDays(payload[0]?.value ?? 0)}</strong>
      </div>
    </div>
  );
}

export default function ClosureDurationTrend() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await fetchWithAuth(
          `${API_BASE}/ainstein/in/dashboard?chart_key=duration_to_closure_trend`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const payload = await response.json();
        const normalized = normalizeRows(payload);

        if (!cancelled) {
          setRows(normalized);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load duration trend");
          setRows([]);
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="remediation-dashboard-section">
      <article className="remediation-dashboard-card">
        <h3 className="remediation-dashboard-card-title">
          Duration to Closure Trend
        </h3>

        {loading && (
          <div className="remediation-dashboard-loading">
            <span className="remediation-dashboard-loading-spinner" />
            <span>Loading data...</span>
          </div>
        )}

        {!loading && (error || rows.length === 0) && (
          <div className="remediation-dashboard-empty-state">
            {error || "No duration trend data available"}
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <MeasuredChartFrame height={320}>
            {({ width, height }) => (
              <LineChart
                width={width}
                height={height}
                data={rows}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.06)"
                />
                <XAxis
                  dataKey="period"
                  tickFormatter={formatLabel}
                  tick={TICK}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={TICK}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                  tickFormatter={formatDays}
                />
                <Tooltip
                  cursor={{ stroke: "rgba(80,178,255,0.25)", strokeWidth: 1 }}
                  content={<CustomTooltip />}
                />
                <Line
                  type="monotone"
                  dataKey="avgDurationDays"
                  name="Avg. duration"
                  stroke={PURPLE}
                  strokeWidth={2.4}
                  dot={{ r: 3, fill: PURPLE, strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: PURPLE }}
                />
              </LineChart>
            )}
          </MeasuredChartFrame>
        )}
      </article>
    </section>
  );
}